import { Injectable, inject } from '@angular/core';
import { BehaviorSubject, Observable, catchError, take, tap, throwError } from 'rxjs';

import { CatalogItem } from '../common/CatalogItem';
import { PagedResult, PagingParams, pagingToHttpParams } from '../common/PagedResult';
import { AppProfileService } from './profile.service';
import { ApiServiceBase } from './ApiServiceBase';

/**
 * #652: the „Merkliste" — the signed-in user's bookmarked catalog listings.
 *
 * What is bookmarked is a LISTING (`CatalogSku.skuId`), not a product across sellers —
 * the same id both frontends route to on `catalog-item/:id`. The list is profile-scoped
 * server-side, so two secondary users of one customer keep separate lists.
 *
 * <b>Anonymous visitors (backend #652 decision 3a).</b> `/search` and `/catalog-item/:id`
 * are public, but the API is `[Authorize]` and the first cut shows the bookmark icon to
 * signed-in users only. Callers gate on the profile; this service simply never requests
 * anything without one.
 *
 * <b>The id set (decision 4).</b> `GET api/bookmark/ids` is loaded once and kept in
 * {@link bookmarkedIds$}, so a results page of up to 500 cards costs one request, not one
 * per card — and the catalog read itself stays anonymous and cacheable. A failing load
 * leaves the set empty: every icon renders unmarked and the search results are otherwise
 * untouched.
 */
@Injectable({
  providedIn: 'root',
})
export class BookmarkService extends ApiServiceBase {
  private profileService = inject(AppProfileService);

  protected readonly path = 'bookmark';

  private readonly _bookmarkedIds = new BehaviorSubject<ReadonlySet<string>>(new Set<string>());

  /** The bookmarked listing ids, for the filled/outlined icon on a catalog card. */
  public readonly bookmarkedIds$: Observable<ReadonlySet<string>> = this._bookmarkedIds.asObservable();

  private idsLoaded = false;
  private idsLoading = false;

  /**
   * Loads the id set on first use and keeps it. Called from the components that render a
   * bookmark icon (`ngOnInit`), not from the constructor, so merely injecting the service
   * issues no request.
   *
   * Without a profile nothing is requested and the load is NOT marked done, so the next
   * caller after a sign-in — the components re-initialise on the post-login navigation —
   * picks the list up. A failed request is not marked done either, for the same reason.
   */
  public ensureIdsLoaded(): void {
    if (this.idsLoaded || this.idsLoading) {
      return;
    }
    this.idsLoading = true;

    this.profileService.currentProfile$.pipe(take(1)).subscribe({
      next: (profile) => {
        if (!profile?.id) {
          this.idsLoading = false;
          return;
        }
        this.getIds().subscribe({
          next: (ids) => {
            this.idsLoaded = true;
            this.idsLoading = false;
            this._bookmarkedIds.next(new Set(ids ?? []));
          },
          // Deliberately silent: the icons stay unmarked, the results list keeps working.
          error: () => (this.idsLoading = false),
        });
      },
      error: () => (this.idsLoading = false),
    });
  }

  public isBookmarked(catalogSkuId: string): boolean {
    return this._bookmarkedIds.value.has(catalogSkuId);
  }

  /**
   * Flips the bookmark of one listing. The local set changes FIRST so the icon reacts on
   * the click rather than on the response; a failing request puts it back and the error
   * surfaces to the caller (which toasts it).
   *
   * The returned Observable is cold — hand it to `[asyncClick]`, which owns the
   * subscription and drops the second click of a double-click while the first is in
   * flight. The endpoints are idempotent anyway (#652 slice B), so a duplicate that does
   * get through is harmless.
   */
  public toggle(catalogSkuId: string): Observable<unknown> {
    const shouldBookmark = !this.isBookmarked(catalogSkuId);
    this.applyLocal(catalogSkuId, shouldBookmark);

    const url = `${this.getUrl()}/${catalogSkuId}`;
    const request$ = shouldBookmark
      ? this.http.post(url, null)
      : this.http.delete(url);

    return request$.pipe(
      take(1),
      catchError((error) => {
        this.applyLocal(catalogSkuId, !shouldBookmark);
        return throwError(() => error);
      }),
    );
  }

  /**
   * The bookmarked listings as catalog items, in the #648 `PagedResult` envelope. Listings
   * that no longer resolve (deactivated, deleted, #509) are simply absent — the page shows
   * fewer entries rather than an error tile.
   */
  public getBookmarks(paging?: PagingParams): Observable<PagedResult<CatalogItem>> {
    return this.http
      .get<PagedResult<CatalogItem>>(this.getUrl(), { params: pagingToHttpParams(paging) })
      .pipe(take(1));
  }

  /** Empties the whole list — the user's own handle on the data (#652 slice C, Art. 17). */
  public clearAll(): Observable<unknown> {
    return this.http.delete(this.getUrl()).pipe(
      take(1),
      tap(() => this._bookmarkedIds.next(new Set<string>())),
    );
  }

  private getIds(): Observable<string[]> {
    return this.http
      .get<string[]>(this.getUrl() + '/ids')
      .pipe(take(1));
  }

  /** Replaces the set rather than mutating it, so subscribers see a new value. */
  private applyLocal(catalogSkuId: string, bookmarked: boolean): void {
    const next = new Set(this._bookmarkedIds.value);
    if (bookmarked) {
      next.add(catalogSkuId);
    } else {
      next.delete(catalogSkuId);
    }
    this._bookmarkedIds.next(next);
  }
}
